import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, EmptyState, Field, Input, PageHeader, Select, Spinner } from '@aigovops/design-system';
import type { Framework, RiskItem } from '@aigovops/core';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { listFrameworks, listRegister } from '../lib/api';
import { fmtDate } from '../lib/format';

/**
 * /w/:slug/app/register/citations — the citation chain.
 *
 * brand-guide Pillar 2 ("Every claim, sourced. Board-ready exports include
 * the citation chain.") + §6.5.2 FreshnessStamp aggregate deep-link
 * ("see chain →") land here. One row per register item with source URL,
 * source type, effective date, framework refs, and last-updated.
 *
 * Reviewer attribution defaults to Steve Nielsen (sole editor per the
 * launch roster); when the roster grows and proposed_changes carries
 * per-item reviewer FKs, this view will surface the last-approving
 * reviewer directly.
 */

const PAGE_SIZE = 500; // Fits current + planned register on one page

function parseJsonArray(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function RegisterCitations() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const frameworkFilter = params.get('framework') ?? 'all';

  const registerReq = useWorkspaceQuery(
    (slug) => queryKeys.register(slug),
    () => listRegister({ limit: PAGE_SIZE }),
  );
  const frameworksReq = useWorkspaceQuery(
    (slug) => queryKeys.allFrameworks(slug),
    () => listFrameworks(),
  );

  const items: RiskItem[] = registerReq.data?.data ?? [];
  const frameworks: Framework[] = frameworksReq.data ?? [];
  const frameworkNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of frameworks) m.set(f.framework_id, f.short_name || f.name);
    return m;
  }, [frameworks]);

  const rows = useMemo(() => {
    let list = items;
    if (frameworkFilter !== 'all') {
      list = list.filter((it) => parseJsonArray(it.framework_refs).includes(frameworkFilter));
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(
        (it) =>
          it.item_id.toLowerCase().includes(s) ||
          it.title.toLowerCase().includes(s) ||
          (it.source_url ?? '').toLowerCase().includes(s),
      );
    }
    // Newest source first
    return [...list].sort((a, b) => {
      const ad = a.effective_date ?? a.updated_at;
      const bd = b.effective_date ?? b.updated_at;
      return (bd ?? '').localeCompare(ad ?? '');
    });
  }, [items, frameworkFilter, search]);

  if (registerReq.loading || frameworksReq.loading) return <Spinner label="Loading citation chain" />;
  if (registerReq.error) return <Alert variant="error">{registerReq.error}</Alert>;
  if (frameworksReq.error) return <Alert variant="error">{frameworksReq.error}</Alert>;

  const totalItems = items.length;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <PageHeader
        title="Citation chain"
        subtitle={`Every register item, sourced. ${totalItems} items across ${frameworks.length} frameworks.`}
      />

      <section
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          fontFamily: 'var(--font-ui)',
          display: 'grid',
          gap: 'var(--space-3)',
        }}
      >
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-3)',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
            alignItems: 'end',
          }}
        >
          <Field id="cit-search" label="Search">
            <Input
              placeholder="Item ID, title, or source URL…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </Field>
          <Field id="cit-framework" label="Framework">
            <Select
              value={frameworkFilter}
              onChange={(e) => {
                const v = e.currentTarget.value;
                const next = new URLSearchParams(params);
                if (v === 'all') next.delete('framework');
                else next.set('framework', v);
                setParams(next, { replace: true });
              }}
            >
              <option value="all">All frameworks</option>
              {frameworks.map((f) => (
                <option key={f.framework_id} value={f.framework_id}>
                  {f.short_name || f.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text-muted)',
          }}
        >
          Showing {rows.length} of {totalItems} · Reviewer attribution: <strong>Steve Nielsen</strong>{' '}
          (GRC lead){' '}
          <Link to="/editors/s-nielsen" style={{ color: 'var(--text-muted)' }}>
            (editor card →)
          </Link>
        </div>
      </section>

      {rows.length === 0 ? (
        <EmptyState
          title="No register items match this filter."
          description="Try clearing the framework filter or search."
        />
      ) : (
        <section
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            overflowX: 'auto',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-2xs)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                <Th>Item</Th>
                <Th>Title</Th>
                <Th>Frameworks</Th>
                <Th>Source</Th>
                <Th>Type</Th>
                <Th>Effective</Th>
                <Th>Updated</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const refs = parseJsonArray(row.framework_refs).map((id) => frameworkNameById.get(id) ?? id);
                return (
                  <tr key={row.item_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <Td>
                      <Link to={`../register/${row.item_id}`} style={{ color: 'var(--text)' }}>
                        {row.item_id}
                      </Link>
                    </Td>
                    <Td>{row.title}</Td>
                    <Td>{refs.join(' · ')}</Td>
                    <Td>
                      {row.source_url ? (
                        <a
                          href={row.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--text)' }}
                        >
                          {truncate(row.source_url, 48)}
                        </a>
                      ) : (
                        <span style={{ color: 'var(--text-dim)' }}>—</span>
                      )}
                    </Td>
                    <Td>{row.source_type}</Td>
                    <Td>{row.effective_date ? fmtDate(row.effective_date) : '—'}</Td>
                    <Td>{fmtDate(row.updated_at)}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      style={{
        textAlign: 'left',
        padding: 'var(--space-2) var(--space-3)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontSize: 'var(--font-size-3xs)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td
      style={{
        padding: 'var(--space-2) var(--space-3)',
        verticalAlign: 'top',
        color: 'var(--text)',
      }}
    >
      {children}
    </td>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
